import React, { useEffect, useState, useCallback, } from 'react';
import { createPortal } from 'react-dom';
import { SettingsButton } from './SettingsButton/SettingsButton';
import { CCButton } from './CCButton/CCButton';
import { SettingsPopover } from './SettingsPopover/SettingsPopover';
import { SettingsContext, } from '../providers/SettingsProvider';
import { useOutsideClick } from '../hooks/useOutsideClick';
const useQuality = (props, closePopover) => {
    const { selectedQuality, qualities, onQualityChange, config } = props;
    const setQualityLevel = useCallback((level) => {
        const nextQuality = qualities.find((q) => q.level === level);
        if (nextQuality) {
            onQualityChange(nextQuality);
        }
        closePopover();
    }, [closePopover, onQualityChange, qualities]);
    return config.qualityEnabled && qualities.length > 1 && selectedQuality
        ? {
            selectedQuality,
            qualities,
            setQualityLevel,
        }
        : undefined;
};
const useSubtitles = (props, closePopover) => {
    const { config } = props;
    const { subtitles, selectedSubtitleValue, subtitlesEnabled, onSubtitleChange, onSubtitlesEnabledChange, } = config;
    const setSelectedValue = useCallback((value) => {
        onSubtitleChange?.(value);
        if (subtitlesEnabled === false) {
            onSubtitlesEnabledChange?.(true);
        }
        closePopover();
    }, [onSubtitleChange, onSubtitlesEnabledChange, subtitlesEnabled, closePopover]);
    const setEnabled = useCallback((enabled) => {
        onSubtitlesEnabledChange?.(enabled);
        closePopover();
    }, [onSubtitlesEnabledChange, closePopover]);
    if (!subtitles?.length) {
        return undefined;
    }
    return {
        options: subtitles,
        selectedValue: selectedSubtitleValue ?? null,
        enabled: Boolean(subtitlesEnabled),
        setSelectedValue,
        setEnabled,
    };
};
const usePlaybackRate = (props, closePopover) => {
    const { onSpeedChange } = props;
    const [playbackRate, setRate] = useState(1);
    const setPlaybackRate = useCallback((value) => {
        setRate(value);
        onSpeedChange(value);
        closePopover();
    }, [closePopover, onSpeedChange]);
    return {
        playbackRate,
        setPlaybackRate,
    };
};
const useVisibility = (onPopoverVisibilityChange, mainUIVisible, onPopoverToggle) => {
    const [isPopoverOpen, setPopoverOpen] = useState(false);
    const setVisibility = useCallback((isOpen) => {
        setPopoverOpen(isOpen);
        if (onPopoverVisibilityChange) {
            onPopoverVisibilityChange(isOpen);
        }
    }, [onPopoverVisibilityChange]);
    const closePopover = useCallback(() => {
        onPopoverToggle(false);
        setVisibility(false);
    }, [setVisibility, onPopoverToggle]);
    const togglePopover = useCallback(() => {
        onPopoverToggle(!isPopoverOpen);
        setVisibility(!isPopoverOpen);
    }, [isPopoverOpen, setVisibility, onPopoverToggle]);
    useEffect(() => {
        if (!mainUIVisible) {
            setVisibility(false);
        }
    }, [mainUIVisible, setVisibility]);
    return {
        setVisibility,
        closePopover,
        isPopoverOpen,
        togglePopover,
    };
};
export const ModuleUI = (props) => {
    const { buttonContainer, popoverContainer, ccButtonContainer, config, isFullScreen, mainUIVisible, onPopoverToggle, } = props;
    const { onPopoverVisibilityChange, speedEnabled, subtitlesEnabled } = config;
    const { isPopoverOpen, setVisibility, closePopover, togglePopover } = useVisibility(onPopoverVisibilityChange, mainUIVisible, onPopoverToggle);
    const qualityState = useQuality(props, closePopover);
    const subtitlesState = useSubtitles(props, closePopover);
    const { playbackRate, setPlaybackRate } = usePlaybackRate(props, closePopover);
    const shouldRenderGear = Boolean(qualityState || speedEnabled);
    const hasCCButton = Boolean(config.subtitles?.length);
    useOutsideClick(popoverContainer, buttonContainer, isPopoverOpen, () => {
        onPopoverToggle(false);
        setVisibility(false);
    });
    if (!shouldRenderGear && !hasCCButton) {
        return null;
    }
    const handleCCToggle = () => {
        config.onSubtitlesEnabledChange?.(!subtitlesEnabled);
    };
    return (React.createElement(SettingsContext.Provider, { value: {
            config,
            playbackRate,
            setPlaybackRate,
            qualityState,
            subtitlesState,
        } },
        shouldRenderGear &&
            createPortal(React.createElement(SettingsButton, { onClick: togglePopover, big: isFullScreen }), buttonContainer),
        shouldRenderGear &&
            createPortal(React.createElement(SettingsPopover, { isOpen: isPopoverOpen }), popoverContainer),
        hasCCButton &&
            createPortal(React.createElement(CCButton, { big: isFullScreen, enabled: Boolean(subtitlesEnabled), onClick: handleCCToggle }), ccButtonContainer)));
};
//# sourceMappingURL=ModuleUI.js.map